import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useSearchParams} from 'react-router-dom';

import {selectUser, useAppSelector} from '@/app/store';
import {useLoading} from '@/app/zustand/useLoading';
import {SPLIT_BILLS_URL} from '@/constants';
import {LINKS} from '@/constants/LinksConstants';
import {useGetGroupQuery} from '@/dataSource/src/api/groups/api';
import {CMemberStatus} from '@/dataSource/src/api/groups/entities';
import {useGroupsRepository} from '@/dataSource/src/api/groups/useGroupsRepository';
import {useLazyGetDefaultPaymentLinkQuery} from '@/dataSource/src/api/payment-link/v2/api';
import {
  IFollowUpOptionsGroup,
  ISplitBill,
  ISplitBillGroup,
} from '@/dataSource/src/api/split-bills/entities';
import {useSplitBillsRepository} from '@/dataSource/src/api/split-bills/useRepository';
import {useCreateSplitBillsLinkMutation} from '@/dataSource/src/api/splits/api';
import {WebViewService} from '@/services/webview/WebViewService';
import {MODAK_DEVELOPMENT_SCHEME, MODAK_SCHEME} from '@/utils/buildDeeplink';
import {createPaymentUrl} from '@/utils/createPaymentUrl';
import {isDevelopment} from '@/utils/environment';
import logError from '@/utils/errors/logError';

import {useGroupsTrackers} from '../groups/trackers';
import {DOWNLOAD_APP_URL} from '../onboarding/pages/DownloadApp/models/constants';

import {
  buildCreateSplitPayload,
  buildCreateSplitPayloadV2,
} from './buildCreateSplitPayload';
import {
  ADJUST_QUERY_PARAMS,
  CHAT_SESSION_ID,
  GO_LINK_HOST,
  SPLIT_ADD_SKIP_REASONS,
  SPLIT_BILLS_QUERY_PARAMS,
  SPLIT_PUBLIC_URL_TEMPLATE,
  SplitAddSkipReason,
  WEB_VIEW_MESSAGES,
} from './constants';
import {useSplitBillsGroupActions} from './hooks/useSplitBillsGroupActions';
import {
  Message,
  MessageButtonLink,
  MessageButtons,
  MessageFollowUpOption,
  SENDER,
  SplitBillResult,
  SplitBillsViewModelContextType,
} from './interfaces';

const MAX_MESSAGES = 20;
const POLL_MS = 1000;
const MAX_RETRIES = 60;

const fileName = 'SplitBillsViewModelContext.tsx';

const SplitBillsViewModelContext = createContext<
  SplitBillsViewModelContextType | undefined
>(undefined);

export function SplitBillsViewModelProvider({children}: {children: ReactNode}) {
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingControlRef = useRef({
    stoppedByUser: false,
    stoppedInternally: false,
  });
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSupportLoading, setIsSupportLoading] = useState<boolean>(false);
  const [stateSessionId, setStateSessionId] = useState<string | null>(null);
  const [fileObject, setFileObject] = useState<File | null>(null);
  const [fileImagesPreview, setFileImagesPreview] = useState<string | null>(
    null,
  );
  const [isCreatingSplit, setIsCreatingSplit] = useState(false);
  const [isSplitAdded, setIsSplitAdded] = useState(false);
  const [splitCreateError, setSplitCreateError] = useState(false);
  const [lastCreatedSplitId, setLastCreatedSplitId] = useState<string | null>(
    null,
  );
  const [searchParams] = useSearchParams();
  const groupId =
    searchParams.get(SPLIT_BILLS_QUERY_PARAMS.GROUP_ID) ?? undefined;
  const currentParticipantId =
    searchParams.get(SPLIT_BILLS_QUERY_PARAMS.PARTICIPANT_ID) ?? undefined;
  const location = useLocation();
  const {contextPayload, contextMessage} = location.state || {};
  const initialMessage = groupId
    ? t('split_bills_page.split_bills_initial_message')
    : t('split_bills_page.bot_initial_message');

  const {
    handlePostBillSubmitted,
    getUploadedBillResult,
    handleSendMessage,
    handleGetMessages,
    handleFreshchatRestoreId,
  } = useSplitBillsRepository();
  const {data: groupData, refetch: refetchGroupData} = useGetGroupQuery(
    {groupId: groupId ?? ''},
    {skip: !groupId},
  );
  const {shouldShowGroupActions, shouldShowShareLinkCard} =
    useSplitBillsGroupActions(groupId, groupData);

  const ownerModakUserId = groupData?.members?.find(
    m => m.split_user_id === groupData?.group?.owner_id,
  )?.modak_user_id;

  const currentModakUserId = groupData?.members?.find(
    m => m.split_user_id === currentParticipantId,
  )?.modak_user_id;

  const {
    trackViewMoResponseError,
    trackApiMoAddSplitError,
    trackApiMoAddSplitSuccess,
    trackViewMoMessageReceived,
    trackViewMoSplitProposal,
    trackSelectMoMessageSent,
    trackSelectMoSplitProposalConfirm,
    trackSelectMoSplitProposalUndo,
  } = useGroupsTrackers(groupId, ownerModakUserId, currentModakUserId);

  const {handleRecordAnonymousExpense} = useGroupsRepository();
  const [triggerCreateSplitBillsLink] = useCreateSplitBillsLinkMutation();
  const [triggerGetDefaultPaymentLink] = useLazyGetDefaultPaymentLinkQuery();
  const user = useAppSelector(selectUser);

  const {setSkipLoading} = useLoading();

  const initialMessageToShow = useMemo(() => {
    if (contextMessage) {
      return contextMessage;
    }
    return initialMessage;
  }, [contextMessage, initialMessage]);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: initialMessageToShow,
      from: SENDER.BOT,
      date: new Date(),
    },
  ]);

  const addMessages = useCallback((...msgs: Message[]) => {
    setMessages(prev => [...prev, ...msgs].slice(-MAX_MESSAGES));
  }, []);

  const logOnSplitAdd = useCallback(
    (reason: SplitAddSkipReason, data?: Record<string, unknown>) => {
      switch (reason) {
        case SPLIT_ADD_SKIP_REASONS.MISSING_SESSION_OR_SPLIT:
          logError(null, {
            severity: 'warning',
            customTitle: 'Mobot - Add split skipped (missing session or split)',
            fileName,
            functionName: 'onSplitAdd',
            data,
          });
          break;

        case SPLIT_ADD_SKIP_REASONS.USER_NOT_LOGGED_IN:
          logError(null, {
            severity: 'warning',
            customTitle: 'Mobot - Add split skipped (user not logged in)',
            fileName,
            functionName: 'onSplitAdd',
            data,
          });
          break;

        case SPLIT_ADD_SKIP_REASONS.NO_DEFAULT_PAYMENT_LINK:
          logError(null, {
            severity: 'warning',
            customTitle: 'Mobot - Add split skipped (no default payment link)',
            fileName,
            functionName: 'onSplitAdd',
            data,
          });
          break;

        default:
          break;
      }
    },
    [],
  );

  const resetSplitCreationState = useCallback(() => {
    setIsSplitAdded(false);
    setSplitCreateError(false);
    setIsCreatingSplit(false);
    setLastCreatedSplitId(null);
  }, []);

  const hideDisclaimer = useMemo(() => {
    return messages.length > 1 || messages.some(m => m.from === SENDER.USER);
  }, [messages]);

  const rewriteDeepLink = useCallback((url: string): string => {
    if (
      isDevelopment &&
      typeof url === 'string' &&
      url.startsWith(MODAK_SCHEME)
    ) {
      return url.replace(MODAK_SCHEME, MODAK_DEVELOPMENT_SCHEME);
    }
    return url;
  }, []);

  const mapButtons = useCallback(
    (
      buttons?: MessageButtons | MessageButtonLink[],
      followUpOptions?: IFollowUpOptionsGroup,
    ): MessageButtons | undefined => {
      const hasFollowUpOptions = Boolean(
        followUpOptions?.in_message?.length || followUpOptions?.other?.length,
      );
      if (!buttons && !hasFollowUpOptions) return undefined;
      const links: MessageButtonLink[] = Array.isArray(buttons)
        ? buttons
        : buttons?.links ?? [];
      return {
        links: links.map(b => ({...b, link: rewriteDeepLink(b.link)})),
        inMessageOptions: followUpOptions?.in_message ?? [],
        otherOptions: followUpOptions?.other ?? [],
      };
    },
    [rewriteDeepLink],
  );

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    setSkipLoading(false);
  }, [setSkipLoading]);

  const buildSplitBillResult = useCallback(
    (
      result: ISplitBill,
      sessionId: string,
    ): {splitBill: SplitBillResult | undefined; hasResult: boolean} => {
      if (!result) return {splitBill: undefined, hasResult: false};

      try {
        const {participants, total, split = {}, payer, description} = result;

        const safeParticipants = (participants ?? []).filter(
          p => split?.[p] !== undefined,
        );

        trackViewMoSplitProposal(groupId, 'one-time');

        resetSplitCreationState();
        stopLoading();

        return {
          splitBill: {
            payer: payer || '',
            participants: safeParticipants,
            split,
            total,
            description: description || '',
          },
          hasResult: true,
        };
      } catch (error) {
        stopLoading();
        logError(error, {
          customTitle: 'Mobot - Error building split bill result',
          fileName,
          functionName: 'buildSplitBillResult',
          extraTags: {session_id: sessionId},
          data: {result},
        });
        return {splitBill: undefined, hasResult: false};
      }
    },
    [trackViewMoSplitProposal, groupId, resetSplitCreationState, stopLoading],
  );

  const buildSplitBillGroupResult = useCallback(
    (
      result: ISplitBillGroup,
      sessionId: string,
    ): {splitBill: SplitBillResult | undefined; hasResult: boolean} => {
      if (!result) return {splitBill: undefined, hasResult: false};

      try {
        trackViewMoSplitProposal(groupId, 'group');
        refetchGroupData();
        const {participants, detail, payers, description} = result;

        const split: Record<string, number> = {};
        participants.forEach(p => {
          split[p.display_name] = p.amount;
        });

        const payer = payers?.[0]?.display_name || '';

        const safeParticipants = participants
          .filter(p => split[p.display_name] !== undefined)
          .map(p => p.display_name);

        resetSplitCreationState();
        stopLoading();

        return {
          splitBill: {
            payer,
            participants: safeParticipants,
            split,
            total: detail.total,
            description: description || '',
          },
          hasResult: true,
        };
      } catch (error) {
        stopLoading();
        logError(error, {
          customTitle: 'Mobot - Error building split bill group result',
          fileName,
          functionName: 'buildSplitBillGroupResult',
          extraTags: {session_id: sessionId},
          data: {result},
        });
        return {splitBill: undefined, hasResult: false};
      }
    },
    [
      groupId,
      refetchGroupData,
      resetSplitCreationState,
      stopLoading,
      trackViewMoSplitProposal,
    ],
  );

  const getDate = useCallback((): string => {
    return Date.now().toString();
  }, []);

  const getBotMessageId = useCallback((): string => {
    return getDate() + '-bot';
  }, [getDate]);

  const addBotTextMessage = useCallback(
    (text: string) => {
      const botMessage: Message = {
        id: getBotMessageId(),
        text,
        from: SENDER.BOT,
        date: new Date(),
      };
      addMessages(botMessage);
    },
    [addMessages, getBotMessageId],
  );

  const canRetry = useCallback((retries: number) => {
    return (
      retries < MAX_RETRIES &&
      !pollingControlRef.current.stoppedInternally &&
      !pollingControlRef.current.stoppedByUser
    );
  }, []);

  const pollForNewMessages = useCallback(
    async (sessionId: string, lastMessageDate: Date, retries: number = 0) => {
      try {
        if (!sessionId) return;
        setSkipLoading(true);
        setIsLoading(true);
        const result = await handleGetMessages(sessionId);
        if (!result || !result.messages || result.messages.length === 0) {
          if (canRetry(retries)) {
            pollTimeoutRef.current = setTimeout(
              () => pollForNewMessages(sessionId, lastMessageDate, retries + 1),
              POLL_MS,
            );
            return;
          }
          throw new Error('Max retries reached without result');
        }

        const newMessages = result.messages.filter(
          m =>
            new Date(m.created_at) > new Date(lastMessageDate) &&
            m.origin !== SENDER.USER.toString(),
        );

        if (newMessages.length > 0) {
          const formattedMessages: Message[] = newMessages?.map(m => {
            const splitResult = m.split_response?.one_off
              ? buildSplitBillResult(m.split_response.one_off, sessionId)
              : m.split_response?.group
                ? buildSplitBillGroupResult(m.split_response.group, sessionId)
                : undefined;

            trackViewMoMessageReceived(null, m.text);

            return {
              id: m.created_at,
              text: m.text ?? '',
              from: SENDER.BOT,
              result: !!m.split_response?.one_off || !!m.split_response?.group,
              splitBill: splitResult?.splitBill,
              rawSplitBill: m.split_response?.one_off,
              rawSplitBillGroup: m.split_response?.group,
              date: new Date(m.created_at),
              showSupport: m.redirect_support,
              buttons: mapButtons(m.buttons, m.follow_up_options),
            };
          });

          addMessages(...formattedMessages);

          stopLoading();
          return;
        }

        if (canRetry(retries)) {
          pollTimeoutRef.current = setTimeout(
            () => pollForNewMessages(sessionId, lastMessageDate, retries + 1),
            POLL_MS,
          );
          return;
        }
        if (
          !pollingControlRef.current.stoppedInternally &&
          !pollingControlRef.current.stoppedByUser
        ) {
          const fallbackMessageNoUpdates: Message = {
            id: getBotMessageId(),
            text: t('split_bills_page.no_updates'),
            from: SENDER.BOT,
            date: new Date(),
          };
          trackViewMoResponseError(sessionId, 'no_updates');
          addMessages(fallbackMessageNoUpdates);
        }
        stopLoading();
      } catch (error) {
        if (canRetry(retries)) {
          pollTimeoutRef.current = setTimeout(
            () => pollForNewMessages(sessionId, lastMessageDate, retries + 1),
            POLL_MS,
          );
          return;
        }
        stopLoading();
        trackViewMoResponseError(sessionId, (error as Error).message);
      }
    },
    [
      setSkipLoading,
      handleGetMessages,
      canRetry,
      stopLoading,
      addMessages,
      buildSplitBillResult,
      buildSplitBillGroupResult,
      trackViewMoMessageReceived,
      mapButtons,
      getBotMessageId,
      t,
      trackViewMoResponseError,
    ],
  );

  const sendMessage = useCallback(
    async (text: string, sessionId?: string, action?: string) => {
      const newUserMessage: Message = {
        id: getDate(),
        text,
        from: SENDER.USER,
        filePreview: fileImagesPreview,
        date: new Date(),
      };

      if (!fileImagesPreview) {
        addMessages(newUserMessage);
      }
      setIsLoading(true);
      setSkipLoading(true);
      try {
        trackSelectMoMessageSent(text);
        const sessionIdToUse = sessionId ?? stateSessionId ?? undefined;
        const contextPayloadString = sessionIdToUse
          ? undefined
          : contextPayload;
        const response = await handleSendMessage({
          session_id: sessionIdToUse,
          context: contextPayloadString,
          text: text,
          ...(groupId && {
            splits: {group_id: groupId, split_user_id: currentParticipantId},
          }),
          ...(action ? {action} : {}),
        });

        if (response?.session_id) {
          sessionStorage.setItem(CHAT_SESSION_ID, response.session_id);
          setStateSessionId(response.session_id);
        }

        const lastMessageDate = newUserMessage.date;

        pollForNewMessages(response?.session_id, lastMessageDate);
      } catch (error) {
        setIsLoading(false);
        const fallbackMessageError: Message = {
          id: getBotMessageId(),
          text: t('split_bills_page.oops'),
          from: SENDER.BOT,
          date: new Date(),
        };
        addMessages(fallbackMessageError);
      } finally {
        setSkipLoading(false);
      }
    },
    [
      getDate,
      fileImagesPreview,
      setSkipLoading,
      addMessages,
      trackSelectMoMessageSent,
      stateSessionId,
      contextPayload,
      handleSendMessage,
      groupId,
      currentParticipantId,
      pollForNewMessages,
      getBotMessageId,
      t,
    ],
  );

  const pollUntilComplete = useCallback(
    async (sessionId: string, text: string, retries: number = 0) => {
      try {
        if (!sessionId) return;
        setSkipLoading(true);
        const result = await getUploadedBillResult(sessionId);

        if (!result) {
          if (canRetry(retries)) {
            pollTimeoutRef.current = setTimeout(
              () => pollUntilComplete(sessionId, text, retries + 1),
              POLL_MS,
            );
            return;
          }
          throw new Error('Max retries reached without result');
        }

        if (result.is_complete) {
          await sendMessage(text, sessionId);
          setSkipLoading(false);
          return;
        }

        if (result.has_error) {
          setIsLoading(false);
          const errorMessage: Message = {
            id: getBotMessageId(),
            text: t('split_bills_page.file_upload_error'),
            from: SENDER.BOT,
            date: new Date(),
          };

          addMessages(errorMessage);
          const error = new Error('Error processing uploaded file');
          logError(error, {
            customTitle: 'Mobot - Error processing uploaded file',
            fileName,
            functionName: 'pollUntilComplete',
            extraTags: {session_id: sessionId},
            data: {result},
          });
          return;
        }

        pollTimeoutRef.current = setTimeout(
          () => pollUntilComplete(sessionId, text),
          POLL_MS,
        );
      } catch (error) {
        if (canRetry(retries)) {
          pollTimeoutRef.current = setTimeout(
            () => pollUntilComplete(sessionId, text, retries + 1),
            POLL_MS,
          );
          return;
        }
        setSkipLoading(false);
        logError(error, {
          customTitle: 'Mobot - Error polling for completion',
          fileName,
          functionName: 'pollUntilComplete',
          extraTags: {session_id: sessionId},
        });
      }
    },
    [
      addMessages,
      canRetry,
      getBotMessageId,
      getUploadedBillResult,
      sendMessage,
      setSkipLoading,
      t,
    ],
  );

  const sendMessageWithFile = useCallback(
    async (text: string) => {
      if (!fileObject) return;

      const newUserMessage: Message = {
        id: getDate(),
        text,
        from: SENDER.USER,
        filePreview: fileImagesPreview,
        date: new Date(),
      };
      addMessages(newUserMessage);
      setSkipLoading(true);
      setIsLoading(true);
      setFileImagesPreview(null);
      setFileObject(null);
      const uploadedSessionId = await handlePostBillSubmitted(
        {file: fileObject},
        stateSessionId,
      );

      if (!uploadedSessionId) {
        setIsLoading(false);
        return;
      }

      setStateSessionId(uploadedSessionId);

      await pollUntilComplete(uploadedSessionId, text);
    },
    [
      addMessages,
      fileImagesPreview,
      fileObject,
      getDate,
      handlePostBillSubmitted,
      pollUntilComplete,
      setSkipLoading,
      stateSessionId,
    ],
  );

  const handleUserSendMessage = useCallback(
    async (text: string) => {
      resetSplitCreationState();
      pollingControlRef.current.stoppedInternally = false;
      pollingControlRef.current.stoppedByUser = false;

      if (fileObject) {
        await sendMessageWithFile(text);
      } else {
        await sendMessage(text);
      }
    },
    [fileObject, resetSplitCreationState, sendMessage, sendMessageWithFile],
  );

  const onFollowUpOptionPress = useCallback(
    async (option: MessageFollowUpOption, messageId: string) => {
      if (!option?.label) return;

      setMessages(prev =>
        prev.map(m =>
          m.id === messageId
            ? {...m, selectedInMessageOption: option.label}
            : m,
        ),
      );

      resetSplitCreationState();
      pollingControlRef.current.stoppedInternally = false;
      pollingControlRef.current.stoppedByUser = false;
      await sendMessage(option.label, undefined, option.action);
    },
    [resetSplitCreationState, sendMessage],
  );

  const onButtonPress = useCallback(() => {
    window.location.href = SPLIT_BILLS_URL;
  }, []);

  const onClearConversationPress = useCallback(() => {
    resetSplitCreationState();
    setStateSessionId(null);
    setMessages([
      {
        id: '1',
        text: initialMessageToShow,
        from: SENDER.BOT,
        date: new Date(),
      },
    ]);
  }, [resetSplitCreationState, initialMessageToShow]);

  const addSplitErrorBotMessage = useCallback(() => {
    addBotTextMessage(t('split_bills_page.split_error_bot_message'));
  }, [addBotTextMessage, t]);

  const fetchDefaultPaymentLinkUrl = useCallback(
    async (userId: string): Promise<string> => {
      try {
        const result = await triggerGetDefaultPaymentLink().unwrap();
        const paymentLinkId = result?.default_payment_link?.id;
        return createPaymentUrl({paymentLinkId});
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error fetching default payment link',
          fileName,
          functionName: 'fetchDefaultPaymentLinkUrl',
          extraTags: {user_id: userId},
        });
        return '';
      }
    },
    [triggerGetDefaultPaymentLink],
  );

  const createSplitLink = useCallback(
    async (params: {
      sessionId: string;
      rawSplitBill?: ISplitBill;
      rawSplitBillGroup?: ISplitBillGroup;
      paymentLink: string;
    }): Promise<string | undefined> => {
      if (params.rawSplitBill) {
        const body = buildCreateSplitPayload({
          session_id: params.sessionId,
          splitBill: params.rawSplitBill,
          owner: {
            id: user?.id,
            name: user?.firstName ?? user?.name ?? '',
          },
          payment_link: params.paymentLink,
          // TODO: remove this once we have the backend ready.
          public_url: SPLIT_PUBLIC_URL_TEMPLATE,
          referral_code: user?.referral_code_owned ?? undefined,
        });
        try {
          const result = await triggerCreateSplitBillsLink(body).unwrap();
          trackApiMoAddSplitSuccess(null, 'one-time');
          return result?.id;
        } catch (error) {
          trackApiMoAddSplitError(null, 'one-time', (error as Error).message);
          logError(error, {
            customTitle: 'Mobot - Error creating split link',
            fileName,
            functionName: 'createSplitLink',
            extraTags: {session_id: params.sessionId},
          });
          return undefined;
        }
      }

      if (params.rawSplitBillGroup) {
        const body = buildCreateSplitPayloadV2({
          created_by: currentParticipantId ?? user?.id ?? '',
          splitBillGroup: params.rawSplitBillGroup,
        });
        try {
          const result = await handleRecordAnonymousExpense(groupId, body);
          trackApiMoAddSplitSuccess(groupId, 'group');
          return result?.split?.id;
        } catch (error) {
          trackApiMoAddSplitError(groupId, 'group', (error as Error).message);
          logError(error, {
            customTitle: 'Mobot - Error creating split link V2',
            fileName,
            functionName: 'createSplitLink',
            extraTags: {session_id: params.sessionId},
          });
          return undefined;
        }
      }
    },
    [
      user?.id,
      user?.firstName,
      user?.name,
      user?.referral_code_owned,
      triggerCreateSplitBillsLink,
      trackApiMoAddSplitSuccess,
      trackApiMoAddSplitError,
      currentParticipantId,
      handleRecordAnonymousExpense,
      groupId,
    ],
  );

  const onSplitAdd = useCallback(async () => {
    resetSplitCreationState();
    const sessionId = stateSessionId ?? sessionStorage.getItem(CHAT_SESSION_ID);
    const lastSplitMessage = [...messages]
      .reverse()
      .find(m => m.rawSplitBill != null || m.rawSplitBillGroup != null);
    const rawSplitBill = lastSplitMessage?.rawSplitBill;
    const rawSplitBillGroup = lastSplitMessage?.rawSplitBillGroup;

    trackSelectMoSplitProposalConfirm(
      groupId,
      rawSplitBill ? 'one-time' : 'group',
    );

    if (!sessionId || (!rawSplitBill && !rawSplitBillGroup)) {
      logOnSplitAdd(SPLIT_ADD_SKIP_REASONS.MISSING_SESSION_OR_SPLIT, {
        hasSession: !!sessionId,
        hasRawSplitBill: !!rawSplitBill,
        hasRawSplitBillGroup: !!rawSplitBillGroup,
      });
      return;
    }

    setIsCreatingSplit(true);
    setSkipLoading(true);
    try {
      const paymentLink = user?.id
        ? await fetchDefaultPaymentLinkUrl(user?.id)
        : undefined;
      if (!paymentLink && user?.id) {
        logOnSplitAdd(SPLIT_ADD_SKIP_REASONS.NO_DEFAULT_PAYMENT_LINK, {
          user_id: user?.id,
        });
        setSplitCreateError(true);
        addSplitErrorBotMessage();
        return;
      }

      const groupMembers = groupData?.members ?? [];

      const splitId = await createSplitLink({
        sessionId,
        rawSplitBill,
        rawSplitBillGroup,
        paymentLink,
      });

      if (splitId) {
        setIsSplitAdded(true);
        setLastCreatedSplitId(splitId);
        const participants = rawSplitBill
          ? rawSplitBill.participants
          : rawSplitBillGroup?.participants.map(p => p.display_name);

        const inactiveParticipants = (participants ?? []).filter(
          p =>
            p !== 'You' &&
            groupMembers.some(
              m => m.display_name === p && m.status !== CMemberStatus.ACTIVE,
            ),
        );

        const brandNewParticipants = (participants ?? []).filter(
          p => p !== 'You' && !groupMembers.some(m => m.display_name === p),
        );

        const hasInactiveMembers = inactiveParticipants.length > 0;
        const hasBrandNewMembers = brandNewParticipants.length > 0;

        if (hasBrandNewMembers) {
          addBotTextMessage(
            t('split_bills_page.success.split_success_bot_message', {
              participants: brandNewParticipants.join(' and '),
            }),
          );
        } else if (hasInactiveMembers) {
          addBotTextMessage(
            t('split_bills_page.success.added_expense_members_invite_pending', {
              participants: inactiveParticipants.join(' and '),
            }),
          );
        } else {
          addBotTextMessage(
            t('split_bills_page.success.added_expense_without_new_members'),
          );
        }
      } else {
        setSplitCreateError(true);
        addSplitErrorBotMessage();
      }
    } catch (error) {
      trackApiMoAddSplitError(
        groupId,
        rawSplitBill ? 'one-time' : 'group',
        (error as Error).message,
      );
      setSplitCreateError(true);
      addSplitErrorBotMessage();
      logError(error, {
        customTitle: 'Mobot - Error in onSplitAdd',
        fileName,
        functionName: 'onSplitAdd',
        extraTags: {session_id: sessionId},
      });
    } finally {
      setIsCreatingSplit(false);
      setSkipLoading(false);
    }
  }, [
    resetSplitCreationState,
    stateSessionId,
    messages,
    trackSelectMoSplitProposalConfirm,
    groupId,
    setSkipLoading,
    logOnSplitAdd,
    user?.id,
    fetchDefaultPaymentLinkUrl,
    groupData?.members,
    createSplitLink,
    addSplitErrorBotMessage,
    addBotTextMessage,
    t,
    trackApiMoAddSplitError,
  ]);

  const onSplitUndo = useCallback(() => {
    resetSplitCreationState();
    addBotTextMessage(t('split_bills_page.split_not_added_copy'));
    trackSelectMoSplitProposalUndo(groupId);
  }, [
    addBotTextMessage,
    groupId,
    resetSplitCreationState,
    t,
    trackSelectMoSplitProposalUndo,
  ]);

  const handleSaveFileImage = useCallback(async (file: File) => {
    if (!file) return;
    setFileObject(file);
  }, []);

  const stopMessage = useCallback(() => {
    pollingControlRef.current.stoppedByUser = true;
    pollingControlRef.current.stoppedInternally = true;
    addMessages({
      id: getBotMessageId(),
      text: t('split_bills_page.generation_stopped'),
      from: SENDER.BOT,
      date: new Date(),
    });
    setFileObject(null);
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    stopLoading();
  }, [addMessages, getBotMessageId, stopLoading, t]);

  const openFreshChat = useCallback(async () => {
    const sessionId = sessionStorage.getItem(CHAT_SESSION_ID);
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      try {
        setIsSupportLoading(true);

        const {restore_id} = await handleFreshchatRestoreId(sessionId);
        if (restore_id) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              status: WEB_VIEW_MESSAGES.OPEN_FRESH_CHAT_WITH_RESTORE_ID,
              restoreId: restore_id,
            }),
          );
          return;
        }
      } catch (error) {
        logError(error, {
          fileName,
          functionName: 'openFreshChat',
          customTitle: 'Mobot - Error opening Freshchat',
          extraTags: {session_id: sessionId},
        });
        const fallbackMessageError: Message = {
          id: getBotMessageId(),
          text: t('split_bills_page.oops_ai_assistant'),
          from: SENDER.BOT,
          date: new Date(),
        };
        addMessages(fallbackMessageError);
      } finally {
        setIsSupportLoading(false);
      }
      return;
    }
    window.open(LINKS.MODAK_SUPPORT, '_blank');
  }, [handleFreshchatRestoreId, addMessages, getBotMessageId, t]);
  const walkThroughTheApp = useCallback(() => {
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          status: WEB_VIEW_MESSAGES.OPEN_WALK_THROUGH_APP,
        }),
      );
      return;
    }
    window.open(DOWNLOAD_APP_URL, '_blank');
  }, []);

  const onButtonDeepLinkPress = useCallback((link: string) => {
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({status: WEB_VIEW_MESSAGES.OPEN_DEEP_LINK, link}),
      );
      return;
    }

    const path = (link || '').replace(
      isDevelopment ? MODAK_DEVELOPMENT_SCHEME : MODAK_SCHEME,
      '',
    );
    const finalUrl = `${GO_LINK_HOST}/${path}?${ADJUST_QUERY_PARAMS}`;
    window.open(finalUrl, '_blank');
  }, []);

  const value = useMemo(
    () => ({
      messages,
      sendMessage: handleUserSendMessage,
      onFollowUpOptionPress,
      isLoading,
      isSplitAdded,
      splitCreateError,
      lastCreatedSplitId,
      onButtonPress,
      onClearConversationPress,
      onSplitAdd,
      onSplitUndo,
      isCreatingSplit,
      handlePostBillSubmitted,
      handleSaveFileImage,
      fileImagesPreview,
      setFileImagesPreview,
      stopMessage,
      openFreshChat,
      walkThroughTheApp,
      hideDisclaimer,
      isSupportLoading,
      onButtonDeepLinkPress,
      shouldShowGroupActions,
      shouldShowShareLinkCard,
    }),
    [
      messages,
      handleUserSendMessage,
      onFollowUpOptionPress,
      isLoading,
      isSplitAdded,
      lastCreatedSplitId,
      splitCreateError,
      onButtonPress,
      onClearConversationPress,
      onSplitAdd,
      onSplitUndo,
      isCreatingSplit,
      handlePostBillSubmitted,
      handleSaveFileImage,
      fileImagesPreview,
      stopMessage,
      openFreshChat,
      walkThroughTheApp,
      hideDisclaimer,
      isSupportLoading,
      onButtonDeepLinkPress,
      shouldShowGroupActions,
      shouldShowShareLinkCard,
    ],
  );

  return (
    <SplitBillsViewModelContext.Provider value={value}>
      {children}
    </SplitBillsViewModelContext.Provider>
  );
}

export const useSplitBillsViewModelProvider = () => {
  const context = useContext(SplitBillsViewModelContext);
  if (!context) {
    throw new Error(
      'useSplitBillsViewModelProvider must be used within SplitBillsViewModelProvider',
    );
  }
  return context;
};
